import type { StarfishIconRef } from '@/app/common/icons/starfish';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import { useFetchHotelStatic } from '@/app/components/hotel-detail/hooks/useFetchHotelStatic';
import {
  FACILITY_ICONS,
  getServicePreviewLabel,
  useExtendedSortedHotelServices,
} from '@/app/components/hotel-detail/hooks/useExtendedSortedHotelServices';
import { buildFacilityCategories, type EvoFacilityCategory } from './buildFacilityCategories';

export type EvoFacilityChip = {
  key: string;
  label: string;
  icon: StarfishIconRef;
};

export type EvoFacilities = {
  mostPopular: EvoFacilityChip[];
  categories: EvoFacilityCategory[];
};

/**
 * What the EVO facilities surfaces (the modal section, the mobile Overview preview and the tab bar) render.
 *
 * With `use_new_facilities` off it is the travel-api `services` list, exactly as before. With it on
 * it is the normalized `facilities` (MPH-237), see buildFacilityCategories, and no "Most popular" row, which
 * product removed until it defines how to pick those facilities. A hotel without facilities gets no section
 * at all, with no fallback to `services`: an empty section is how a broken travel-api response shows.
 */
export function useEvoFacilities(): EvoFacilities {
  const { t } = useTranslation();
  const { localization, searchInfoData } = usePageWrapperData();
  const { data: staticData } = useFetchHotelStatic();
  const services = useExtendedSortedHotelServices(staticData?.services);

  if (localization?.configValuesApp?.useNewFacilities) {
    return {
      mostPopular: [],
      categories: buildFacilityCategories(staticData?.facilities, t, searchInfoData.searchMode),
    };
  }

  return {
    mostPopular: services.map((service) => ({
      key: service.category,
      label: getServicePreviewLabel(service, t),
      icon: FACILITY_ICONS[service.icon],
    })),
    categories: services.map((service) => ({
      key: service.category,
      title: t(service.translationKey),
      icon: FACILITY_ICONS[service.icon],
      items: service.services,
      breakColumn: service.category === 'GENERAL',
    })),
  };
}
